import { Component } from '@theme/component';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer.
   *
   * @param {Event} [event] - The summary click event.
   */
  toggle(event) {
    // Prevent the native <details> toggle so JS is the SINGLE source of truth for the open
    // state. Without this the browser flips `open` on summary click independently of the
    // JS-managed `.menu-open` class: the two desync, and on close the panel is display:none'd
    // instantly, cancelling the close animation. With it, open()/#close()/reset() own the
    // state and the panel stays in layout long enough to animate out.
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Only one drawer open at a time: close the cart drawer when opening the main menu.
    if (details === this.refs.details) {
      const cart = /** @type {(HTMLElement & { close?: () => void }) | null} */ (
        document.querySelector('cart-drawer-component')
      );
      if (typeof cart?.close === 'function') cart.close();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    // JS owns the open attribute now that the native toggle is prevented (see toggle()).
    // Set it (also fires the `toggle` event that drives the shared scroll-lock handler in
    // dialog.js), then commit the panel's hidden initial state with a forced reflow before
    // adding .menu-open — this triggers the clip-path reveal transition WITHOUT relying on
    // requestAnimationFrame (which is paused when the tab is backgrounded).
    details.open = true;
    const panel = details === this.refs.details ? this.refs.menuDrawer : details.querySelector('.menu-drawer__submenu');
    void (panel instanceof HTMLElement ? panel.offsetHeight : details.offsetHeight);

    details.classList.add('menu-open');

    if (target) {
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
    }

    // Wait for the drawer animation to complete, then move focus in (relaxed focus:
    // no hard trap, so keyboard users can still reach the live header controls).
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    onAnimationEnd(drawer || details, () => this.#focusFirst(details), { subtree: false });
  }

  /**
   * Move focus to the first focusable link/button within a container (skips the summary,
   * which lives in the header). Used instead of a hard focus trap.
   *
   * @param {HTMLElement} container
   */
  #focusFirst(container) {
    const focusable = container.querySelector(
      "a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])"
    );
    if (focusable instanceof HTMLElement) focusable.focus();
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    // Remove .menu-open to start the close animation. Crucially we DON'T touch `open` here:
    // the panel must stay in layout (open) while it animates out; reset() removes `open`
    // only after onAnimationEnd, so the close mirrors the open (same easing/duration).
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          // Relaxed focus: restore focus to the summary (the header hamburger/close toggle).
          this.querySelector('summary')?.focus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          // Closing a submenu: move focus back into the main drawer panel.
          this.#focusFirst(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
